import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ArrowRight, BarChart3, Building2, FileText, Shield, Users, Truck, Package } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isRecoveryLocked } from "@/lib/recoveryLock";

const Landing = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Redirect authenticated users to dashboard or driver mode (but verify profile exists first)
  useEffect(() => {
    const checkAndRedirect = async () => {
      if (!loading && user) {
        // AUTH-CADASTRO Ato 1: com a trava de recuperação, quem decide o destino é o RecoveryLockGate
        if (isRecoveryLocked()) return;
        // Verify that profile actually exists before redirecting
        const { data: profile } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (profile) {
          // ✅ FIXED: Respect driver mode flag
          const isDriverMode = localStorage.getItem('gonx-driver-mode') === 'true';
          if (isDriverMode) {
            navigate('/driver');
          } else {
            localStorage.removeItem('gonx-driver-mode');
            navigate('/dashboard');
          }
        } else {
          // Invalid session - clear it and stay on landing
          console.log('🧹 Sessão inválida detectada na Landing - limpando');
          await supabase.auth.signOut();
        }
      }
    };
    
    checkAndRedirect();
  }, [user, loading, navigate]);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-primary rounded-full animate-pulse"></div>
          <div className="w-4 h-4 bg-primary rounded-full animate-pulse animation-delay-150"></div>
          <div className="w-4 h-4 bg-primary rounded-full animate-pulse animation-delay-300"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-industrial">
      {/* Header */}
      <header className="bg-background/80 backdrop-blur-sm border-b border-border sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img 
              src="/gonx-logo.png" 
              alt="GOnX Logo" 
              className="h-10 w-auto"
            />
            <div>
              <h1 className="text-xl font-bold text-primary">GOnX</h1>
              <p className="text-xs text-muted-foreground">Vai e multiplique</p>
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-primary hover:bg-primary-dark text-primary-foreground">
                Acessar Sistema
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 p-2">
              <DropdownMenuItem
                onClick={() => { localStorage.removeItem('gonx-driver-mode'); navigate('/auth'); }}
                className="flex items-center gap-4 p-3 cursor-pointer rounded-md transition-all hover:bg-accent focus:bg-accent"
              >
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Building2 size={24} />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold">GOnX ERP</span>
                  <span className="text-xs text-muted-foreground">Gestão completa</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate('/driver')}
                className="flex items-center gap-4 p-3 cursor-pointer rounded-md transition-all hover:bg-accent focus:bg-accent"
              >
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Truck size={24} />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold">GOnX Driver</span>
                  <span className="text-xs text-muted-foreground">Logística</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6">
              Sistema Completo de 
              <span className="text-primary block mt-2">Gestão Industrial</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Controle total de locação e empréstimo de cilindros de gases industriais. 
              Gerencie estoque, movimentações e relatórios em uma única plataforma.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => { localStorage.removeItem('gonx-driver-mode'); navigate('/auth'); }}
                className="bg-primary hover:bg-primary-dark text-primary-foreground text-lg px-8 py-6"
              >
                Começar Agora
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-primary-foreground text-lg px-8 py-6"
              >
                Ver Demonstração
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Funcionalidades Completas
            </h2>
            <p className="text-lg text-muted-foreground">
              Tudo que você precisa para gerenciar seu negócio industrial
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="p-6 bg-card border-border hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <Package className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-card-foreground ml-3">
                  Controle de Estoque
                </h3>
              </div>
              <p className="text-muted-foreground">
                Gerencie cilindros, equipamentos e produtos com rastreamento completo de movimentações.
              </p>
            </Card>

            <Card className="p-6 bg-card border-border hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <Truck className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-card-foreground ml-3">
                  Locações e Empréstimos
                </h3>
              </div>
              <p className="text-muted-foreground">
                Controle completo de locações com contratos digitais e acompanhamento de devolução.
              </p>
            </Card>

            <Card className="p-6 bg-card border-border hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-card-foreground ml-3">
                  Documentos Digitais
                </h3>
              </div>
              <p className="text-muted-foreground">
                Gere contratos, recibos e relatórios automaticamente com assinatura digital.
              </p>
            </Card>

            <Card className="p-6 bg-card border-border hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-card-foreground ml-3">
                  Gestão de Clientes
                </h3>
              </div>
              <p className="text-muted-foreground">
                Cadastro completo de clientes, funcionários e fornecedores com histórico detalhado.
              </p>
            </Card>

            <Card className="p-6 bg-card border-border hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <BarChart3 className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-card-foreground ml-3">
                  Relatórios e Analytics
                </h3>
              </div>
              <p className="text-muted-foreground">
                Dashboards inteligentes com métricas de desempenho e análises de negócio.
              </p>
            </Card>

            <Card className="p-6 bg-card border-border hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-card-foreground ml-3">
                  Segurança Total
                </h3>
              </div>
              <p className="text-muted-foreground">
                Controle de acesso por usuário, backup automático e proteção de dados empresariais.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-6">
                Por que escolher o GOnX?
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-success/10 p-2 rounded-lg flex-shrink-0">
                    <ArrowRight className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">
                      Aumento da Produtividade
                    </h3>
                    <p className="text-muted-foreground">
                      Automatize processos manuais e reduza tempo gasto em tarefas administrativas.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-success/10 p-2 rounded-lg flex-shrink-0">
                    <ArrowRight className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">
                      Controle Financeiro
                    </h3>
                    <p className="text-muted-foreground">
                      Tenha visão completa de receitas, custos e lucratividade de cada operação.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-success/10 p-2 rounded-lg flex-shrink-0">
                    <ArrowRight className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">
                      Redução de Perdas
                    </h3>
                    <p className="text-muted-foreground">
                      Rastreamento completo evita perdas de equipamentos e atrasos nas devoluções.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-card p-8 rounded-lg">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-card-foreground mb-4">
                  Pronto para começar?
                </h3>
                <p className="text-muted-foreground mb-6">
                  Junte-se às empresas que já revolucionaram sua gestão industrial com o GOnX.
                </p>
                <Button 
                  size="lg"
                  onClick={() => { localStorage.removeItem('gonx-driver-mode'); navigate('/auth'); }}
                  className="bg-primary hover:bg-primary-dark text-primary-foreground w-full"
                >
                  Acessar Sistema Agora
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background border-t border-border py-12 px-4">
        <div className="container mx-auto">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <img 
                src="/gonx-logo.png" 
                alt="GOnX Logo" 
                className="h-8 w-auto"
              />
              <div>
                <h3 className="text-lg font-bold text-primary">GOnX</h3>
                <p className="text-xs text-muted-foreground">Vai e multiplique</p>
              </div>
            </div>
            <p className="text-muted-foreground mb-4">
              Sistema completo de gestão industrial para locação e empréstimo de cilindros de gases.
            </p>
            <p className="text-sm text-muted-foreground">
              © 2024 GOnX. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;